import {
  Object3DEventMap,
  Object3D,
  Group,
  Mesh,
  MeshBasicMaterial,
  Vector3,
  Vector3Tuple,
  Matrix4,
  Box3,
  LineBasicMaterial,
  Line,
  BufferGeometry,
  LineSegments,
  Plane,
  Camera,
  LineDashedMaterial,
  Material,
  BackSide,
  FrontSide,
  DoubleSide,
  PlaneGeometry,
  AlwaysStencilFunc,
  NotEqualStencilFunc,
  IncrementWrapStencilOp,
  DecrementWrapStencilOp,
  ReplaceStencilOp,
  Quaternion,
} from 'three'
import {
  computeBoundsTree,
  disposeBoundsTree,
  acceleratedRaycast,
} from 'three-mesh-bvh'
import { LRUCache } from 'lru-cache'
import { mergePartMeshes, buildPartWireframe, isLeafNode } from './GLTFMerge'
import {
  BACK_RENDER_ORDER,
  BACKSIDE_LAYER,
  DEFAULT_LAYER,
  OVERLAY_RENDER_ORDER,
  FRONT_RENDER_ORDER,
  INTERACTION_LAYER,
  WIREFRAME_LAYER,
  WIRE_RENDER_ORDER,
} from '@/pages/CADPage/constants'
import { TransformControls } from 'three-stdlib'
import { ASMTreeNode, RawAssemblyTree } from '@/state'
import type { Explosions } from '@/state/cad'
import type { Step } from '@/services/queries/operation_steps/types'
import { wireframeMaterial } from './materials'

import { convertCrossSectionMap } from '@/utils/cad'
import { HIGHLIGHT_BLUE } from '@/constants'
import { validate as isUUID } from 'uuid'
import { ImageData } from '../api/client'

import { getVisiblePathOfGroupIdsFast } from '@/lib/cad/getVisiblePathOfGroupIdsFast'

TransformControls.prototype.getRaycaster = function (this: TransformControls) {
  return this['raycaster']
}

TransformControls.prototype.getHelper = function (this: TransformControls) {
  return this['gizmo']['picker']['translate']
}

TransformControls.prototype.getOffset = function (this: TransformControls) {
  return this['offset']
}

Object.defineProperty(TransformControls.prototype, 'isDragging', {
  get: function (this: TransformControls) {
    return this['dragging'] ? true : false
  },
})

BufferGeometry.prototype.computeBoundsTree = computeBoundsTree
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree
Mesh.prototype.raycast = acceleratedRaycast

export type GroupObject =
  | Group
  | Object3D
  | Group<Object3DEventMap>
  | Object3D<Object3DEventMap>

type OriginalPartColors = {
  [key: string]: {
    color: number
    mesh: Mesh
  }
}

type ObjectExplosionsUserData = {
  originalParentName: string
  originalParentWorldPosition: Vector3Tuple
  originalPosition: Vector3Tuple
  originalWorldPosition: Vector3Tuple
}

const ExplosionsUserDataKeys = [
  'originalParentName',
  'originalParentWorldPosition',
  'originalPosition',
  'originalWorldPosition',
]

export class GLTFObject {
  scene: Group<Object3DEventMap>
  totalBytes: number
  optimized: boolean
  originalPartColors: LRUCache<string, OriginalPartColors>
  coloredPartsCache: Set<string>
  transformControls: TransformControls | null
  crossSectionControls: {
    translate: TransformControls | null
    rotate: TransformControls | null
  }
  transformGroup: Group<Object3DEventMap> | null
  lines: Line[]
  lineMaterial: LineBasicMaterial
  partNodeMap: Map<string, ASMTreeNode>
  partObjectMap: Map<string, Object3D>
  objectNodeMap: WeakMap<Object3D, ASMTreeNode>
  materialMap: Map<string, Material>

  private isSetup: boolean
  private isMerged: boolean
  private mapsGenerated: boolean
  private isBlackAndWhiteWireframeEnabled: boolean
  private crossSectionCapObjects: Object3D[]
  private crossSectionCapMaterials: Material[]
  private crossSectionCapGeometries: BufferGeometry[]

  mergePartMeshes: (obj: Object3D) => number
  buildPartWireframe: (obj: Object3D) => LineSegments | undefined
  isLeafNode: (node: ASMTreeNode) => boolean

  constructor(
    scene: Group<Object3DEventMap>,
    totalBytes?: number,
    optimized?: boolean,
  ) {
    this.scene = scene
    this.optimized = Boolean(optimized)
    this.totalBytes = totalBytes || 0
    this.originalPartColors = new LRUCache<string, OriginalPartColors>({
      max: 1000000,
    })
    this.coloredPartsCache = new Set()
    this.transformGroup = null
    this.transformControls = null
    this.crossSectionControls = { translate: null, rotate: null }
    this.lines = []
    this.lineMaterial = new LineBasicMaterial(wireframeMaterial)
    this.partNodeMap = new Map()
    this.partObjectMap = new Map()
    this.objectNodeMap = new Map()
    this.materialMap = new Map()

    this.isSetup = false
    this.isMerged = false
    this.mapsGenerated = false
    this.isBlackAndWhiteWireframeEnabled = false
    this.crossSectionCapObjects = []
    this.crossSectionCapMaterials = []
    this.crossSectionCapGeometries = []

    this.mergePartMeshes = mergePartMeshes.bind(this)
    this.buildPartWireframe = buildPartWireframe.bind(this)
    this.isLeafNode = isLeafNode.bind(this)
  }

  setup(
    assemblyTree: RawAssemblyTree,
    options: {
      mergeModelParts?: boolean
      computeBoundsTree?: boolean
      camera?: Camera
    },
  ) {
    if (this.isSetup) return
    const mergeModelParts = options.mergeModelParts ?? true
    const computeBoundsTree = options.computeBoundsTree ?? false

    // Generate Object Maps
    this.generateObjectMaps(assemblyTree)

    // Merge Model Parts
    if (mergeModelParts && !this.optimized) this.mergeModelParts(assemblyTree)

    // Compute Bounds Tree & Set Layers
    this.scene.traverse((object) => this.postSetup(object, computeBoundsTree))

    // Clear Setup Maps
    this.materialMap.clear()

    this.isSetup = true
  }

  cleanupModel(opts: { computeBoundsTree: boolean }) {
    this.scene.traverse((object) => {
      if (object instanceof Mesh) {
        if (opts.computeBoundsTree) object.geometry.disposeBoundsTree()
      }
    })
  }

  dispose() {
    if (!this.scene) return
    this.blackAndWhiteWireframe(false)
    this.clearCrossSectionCaps()

    // Collect all objects first
    const objectsToDispose: Object3D[] = []
    this.scene.traverse((child) => {
      objectsToDispose.push(child)
    })

    // Now dispose geometries and materials
    for (const child of objectsToDispose) {
      if ((child as any).geometry) {
        ;(child as any).geometry.dispose()
      }
      if ((child as any).material) {
        const material = (child as any).material
        if (Array.isArray(material)) {
          material.forEach((mat: any) => mat.dispose())
        } else {
          material.dispose()
        }
      }
    }

    // Clear all children at once
    this.scene.clear()
  }

  fullReset(imageData: ImageData) {
    this.blackAndWhiteWireframe(false)
    this.resetHiddenParts(imageData.hiddenParts ?? [])
    this.showExplosionLines(imageData.explosions ?? [], false)
    this.resetExplosions(imageData.explosions ?? [])
    this.unhighlightParts(imageData.colorMap ?? [])
    this.clearDragLines()
    this.resetColors()
    this.resetTransparency(imageData.transparentParts ?? [])
    this.resetClippingPlanes()
  }

  postSetup(object: Object3D, computeBoundsTree: boolean) {
    if (object instanceof Mesh && object.userData.isFrontSide) {
      if (computeBoundsTree) object.geometry.computeBoundsTree()
    }

    if (object instanceof Mesh) {
      const mat = object.material as unknown as Material | Material[]

      object.userData.originalMaterial = mat

      if (Array.isArray(mat)) {
        object.userData.originalColor = mat.map((m) => {
          const c = (m as any)?.color
          return c && typeof c.getHex === 'function' ? c.getHex() : null
        })
      } else {
        const c = (mat as any)?.color
        object.userData.originalColor =
          c && typeof c.getHex === 'function' ? c.getHex() : null
      }
    }

    if (object instanceof LineSegments) {
      object.layers.set(WIREFRAME_LAYER)

      if (this.optimized) {
        object.frustumCulled = true
        object.renderOrder = WIRE_RENDER_ORDER
      }
    } else {
      this.setObjectRenderLayer(object)
    }
  }

  private setObjectRenderLayer(object: Object3D) {
    if (object instanceof LineSegments) object.layers.set(WIREFRAME_LAYER)
    else if (object.userData.isBackSide) object.layers.set(BACKSIDE_LAYER)
    else if (object.userData.isFrontSide) object.layers.set(INTERACTION_LAYER)
    else object.layers.set(DEFAULT_LAYER)
  }

  getModelBounds() {
    const bounds = new Box3()
    this.scene.updateMatrixWorld(true)

    this.scene.traverse((object) => {
      if (this.shouldExcludeFromModelBounds(object)) return
      if (object instanceof Mesh) bounds.expandByObject(object)
    })

    return bounds
  }

  private shouldExcludeFromModelBounds(object: Object3D) {
    return (
      object.userData.isCrossSectionCap ||
      object.userData.planeObject ||
      object instanceof TransformControls
    )
  }

  mergeModelParts(assemblyTree: RawAssemblyTree) {
    let nMeshes = 0
    if (this.isMerged) return nMeshes

    Array.from(this.partNodeMap.values()).map((node) => {
      if (node.uuid === assemblyTree.root) return
      const obj = this.partObjectMap.get(node.uuid)
      if (!obj) return

      if (this.isLeafNode(node)) {
        const waireframeNode = this.buildPartWireframe(obj)
        nMeshes += this.mergePartMeshes(obj)
        if (waireframeNode) obj.add(waireframeNode)
      }
    })

    this.isMerged = true
    return nMeshes
  }

  generateObjectMaps(assemblyTree: RawAssemblyTree) {
    if (this.mapsGenerated) return
    const nameNodeMap = new Map<string, ASMTreeNode>()

    assemblyTree.nodes.forEach((node) => {
      this.partNodeMap.set(node.uuid, node)
      nameNodeMap.set(node.instance, node)
    })

    this.scene.traverse((obj) => {
      const node = nameNodeMap.get(obj.name)
      if (node) {
        this.partObjectMap.set(node.uuid, obj)
        this.objectNodeMap.set(obj, node)
      }
    })

    this.mapsGenerated = true
  }

  formatPartName(partName: string) {
    return partName.startsWith('Group:')
      ? partName
      : partName.replace(' ', '_').replace(':', '')
  }

  viewPart(assemblyTree: RawAssemblyTree, target: string) {
    const root = this.partNodeMap.get(assemblyTree.root)
    const part = this.partNodeMap.get(target)
    const object = this.scene.getObjectByName(part?.instance as string)

    if (!object || !part || !root) return

    this.scene.getObjectByName(root.instance)?.traverse((child) => {
      child.layers.disableAll()
    })

    object.traverse((child) => {
      this.setObjectRenderLayer(child)
    })
  }

  setVisibility(partId: string, isVisible: boolean) {
    const part = this.getObjectByName(this.formatPartName(partId))
    if (part) part.visible = isVisible
  }

  setVisibilityForOperationSteps({
    assemblyTree,
    steps,
    hiddenParts,
    activeStep,
    explosions,
    isExplosionLinesEnabled,
    hidePreviousOperations,
    showFullModel,
  }: {
    assemblyTree: RawAssemblyTree
    steps: Step[]
    hiddenParts: string[]
    activeStep?: Step | null
    explosions: Explosions
    isExplosionLinesEnabled: boolean
    hidePreviousOperations: boolean
    showFullModel: boolean
  }) {
    const stepNumber = activeStep?.order_number
    const activeParentId = activeStep?.parent
    const isActive = Boolean(activeStep)

    const firstStepNumber = hidePreviousOperations ? (stepNumber || 0) - 1 : 0

    const assemblyGroupIdsUpToStep = new Set(
      steps
        .filter((s) => s.parent === activeParentId)
        .slice(firstStepNumber, stepNumber)
        .flatMap((s) => {
          return Array.from(
            this.getVisiblePathOfGroupIds({
              assemblyGroupIds: s.assembly_group_ids,
              assemblyTree,
            }),
          )
        }),
    )

    const rootNode = this.partNodeMap.get(assemblyTree.root)

    const visibleExplosions = {}

    // Perform a depth first traversal down the assembly-tree
    // and set the vibility for each node in the tree
    const dfsNodeQueue = [...(rootNode?.children ?? [])]
    const visitedNodes = new Set()

    while (dfsNodeQueue.length > 0) {
      const nodeUUID = dfsNodeQueue.pop()
      while (!nodeUUID) continue

      if (visitedNodes.has(nodeUUID)) continue
      visitedNodes.add(nodeUUID)

      const node = this.partNodeMap.get(nodeUUID)
      if (!node) continue
      const isHiddenPart = hiddenParts.includes(node.uuid)

      const isFullModelVisibleWithHiddenParts =
        showFullModel && isHiddenPart ? !isHiddenPart : showFullModel

      const isVisible =
        isFullModelVisibleWithHiddenParts ||
        (isActive
          ? assemblyGroupIdsUpToStep.has(node.uuid) && !isHiddenPart
          : !isHiddenPart)

      if (isVisible && node.instance in explosions) {
        visibleExplosions[node.instance] = explosions[node.instance]
      }

      this.setVisibility(node.uuid, isVisible)

      node.children.forEach((childUUID) => dfsNodeQueue.push(childUUID))
    }

    this.showExplosionLines(visibleExplosions, isExplosionLinesEnabled, {
      hiddenParts,
    })
  }

  getVisiblePathOfGroupIds({
    assemblyGroupIds,
    assemblyTree,
  }: {
    assemblyGroupIds: string[]
    assemblyTree: RawAssemblyTree
  }) {
    /**
     *
     *  Include all of the parent assembly-group-ids, this will
     *  prevent unintentionally hiding parents because their parents
     *  were hidden.
     *
     *  You can think of this as the "visible path" taken to make a
     *  part in the assembly tree visible
     *
     *  For example, Let's say we have the following assembly tree
     *
     *    - prop-asm
     *        - BrushlessMotor
     *        - Blade
     *
     *  If `prop-asm` is added to step than we'll want to set visibility
     *  to true for `prop-asm`, `BrushlessMotor` and `Blade`.
     *
     *  If only `BrushlessMotor` is added to a step than we'll want to set the
     *  vibility to true for `prop-asm` and `BrushlessMotor`
     */

    return getVisiblePathOfGroupIdsFast({
      assemblyGroupIds,
      assemblyTree,
    })
  }

  getObjectByUUID(uuid: string): Object3D | undefined {
    const obj = this.partObjectMap.get(uuid)
    return obj
  }

  getObjectByName(name: string): Group | Object3D | undefined {
    if (isUUID(name)) {
      const obj = this.getObjectByUUID(name)
      if (obj) return obj
    }
    return this.scene.getObjectByName(name)
  }

  /**
   * highlightPart - Highlights a part in the assembly tree.
   * @param partName
   */
  highlightPart(partId: string, color?: number, internalView?: boolean) {
    const highlightColor = color || HIGHLIGHT_BLUE
    this.setColor(partId, highlightColor, internalView)
  }

  /**
   * unhighlightParts - Unhighlights a part in the assembly tree.
   */
  unhighlightParts(colorMap: { [key: string]: number }) {
    this.resetColors({ colorMap })
  }

  blackAndWhiteWireframe(enabled: boolean) {
    if (this.isBlackAndWhiteWireframeEnabled === enabled) return

    const defaultWireColor =
      typeof wireframeMaterial.color === 'number'
        ? wireframeMaterial.color
        : 0x000000

    // Boost edge contrast and reduce surface/edge z-fighting in B/W mode.
    if (enabled) {
      this.lineMaterial.color.setHex(0x000000)
      this.lineMaterial.toneMapped = false
      this.lineMaterial.polygonOffset = true
      this.lineMaterial.polygonOffsetFactor = -3
      this.lineMaterial.polygonOffsetUnits = -3
    } else {
      this.lineMaterial.color.setHex(defaultWireColor)
      this.lineMaterial.toneMapped = true
      this.lineMaterial.polygonOffset = Boolean(wireframeMaterial.polygonOffset)
      this.lineMaterial.polygonOffsetFactor =
        wireframeMaterial.polygonOffsetFactor ?? -1
      this.lineMaterial.polygonOffsetUnits =
        wireframeMaterial.polygonOffsetUnits ?? -1
    }
    this.lineMaterial.needsUpdate = true

    this.scene.traverse((child) => {
      if (!(child instanceof Mesh)) return
      if (child.userData.isCrossSectionCap) return
      const mesh = child as Mesh
      const currentMaterial = mesh.material as Material | Material[]

      if (enabled) {
        mesh.userData.blackAndWhiteOriginalMaterial = currentMaterial
        const materials = Array.isArray(currentMaterial)
          ? currentMaterial
          : [currentMaterial]
        const nextMaterials = materials.map((material) => {
          const matClone = material.clone()
          const materialClone = matClone as any
          const color = materialClone?.color
          if (color && typeof color.setHex === 'function') {
            color.setHex(0xffffff)
          }

          // Ensure a true white appearance, independent of scene lighting.
          if (
            materialClone?.emissive &&
            typeof materialClone.emissive.setHex === 'function'
          ) {
            materialClone.emissive.setHex(0xffffff)
          }
          if (typeof materialClone?.emissiveIntensity === 'number') {
            materialClone.emissiveIntensity = 1
          }
          if (typeof materialClone?.metalness === 'number') {
            materialClone.metalness = 0
          }
          if (typeof materialClone?.roughness === 'number') {
            materialClone.roughness = 1
          }
          if (typeof materialClone?.toneMapped === 'boolean') {
            materialClone.toneMapped = false
          }

          materialClone.needsUpdate = true
          return matClone
        })

        mesh.material = Array.isArray(currentMaterial)
          ? nextMaterials
          : nextMaterials[0]
        return
      }

      const originalMaterial = mesh.userData.blackAndWhiteOriginalMaterial as
        | Material
        | Material[]
        | undefined
      if (!originalMaterial) return

      const currentMaterials = Array.isArray(currentMaterial)
        ? currentMaterial
        : [currentMaterial]
      const originalMaterials = Array.isArray(originalMaterial)
        ? originalMaterial
        : [originalMaterial]

      currentMaterials.forEach((material) => {
        if (
          !originalMaterials.includes(material) &&
          typeof (material as any)?.dispose === 'function'
        ) {
          ;(material as any).dispose()
        }
      })

      mesh.material = originalMaterial
      delete mesh.userData.blackAndWhiteOriginalMaterial
    })

    this.isBlackAndWhiteWireframeEnabled = enabled
  }

  /**
   * setColor - Sets or resets the color of a part in the CAD model
   * @param partName
   * @returns the new color of the part, or the previous color if the part was already that color
   */
  setColor(partId: string, color: number | string, internalView?: boolean) {
    const object = this.getObjectByName(partId)
    if (!object) return

    const parsedColor =
      typeof color === 'number' ? color : parseInt(color.slice(1), 16)
    const forceWhite = this.isBlackAndWhiteWireframeEnabled

    object.traverse((child) => {
      if (child.type === 'Mesh' && !child.userData.isCrossSectionCap) {
        const mesh = child as Mesh
        const material = mesh.material as MeshBasicMaterial
        const originalMaterial = mesh.userData.originalMaterial as
          | Material
          | Material[]
          | undefined
        const firstOriginalMaterial = Array.isArray(originalMaterial)
          ? (originalMaterial[0] as any)
          : (originalMaterial as any)

        // Clone the material to avoid affecting other meshes sharing it
        const matClone = material.clone()
        const materialClone = matClone as any
        materialClone.color?.setHex(parsedColor)

        // Keep on-hover interaction colors visually consistent with non-B/W mode.
        if (forceWhite) {
          if (
            materialClone?.emissive &&
            typeof materialClone.emissive.setHex === 'function'
          ) {
            materialClone.emissive.setHex(0x000000)
            if (typeof materialClone.emissiveIntensity === 'number') {
              materialClone.emissiveIntensity = 0
            }
          }

          if (
            typeof materialClone?.metalness === 'number' &&
            typeof firstOriginalMaterial?.metalness === 'number'
          ) {
            materialClone.metalness = firstOriginalMaterial.metalness
          }
          if (
            typeof materialClone?.roughness === 'number' &&
            typeof firstOriginalMaterial?.roughness === 'number'
          ) {
            materialClone.roughness = firstOriginalMaterial.roughness
          }
          if (typeof materialClone?.toneMapped === 'boolean') {
            materialClone.toneMapped =
              typeof firstOriginalMaterial?.toneMapped === 'boolean'
                ? firstOriginalMaterial.toneMapped
                : true
          }
        }

        materialClone.needsUpdate = true

        mesh.material = matClone

        if (internalView && mesh.name) {
          mesh.material.depthTest = false
          mesh.renderOrder = OVERLAY_RENDER_ORDER
          mesh.material.needsUpdate = true
        }
      }
    })

    this.coloredPartsCache.add(partId)
  }

  resetColors(options?: { colorMap?: { [key: string]: number } }) {
    const colorMap = options?.colorMap || {}

    this.coloredPartsCache.forEach((partId) => {
      const mapColor = this.resolveMappedColor(partId, colorMap)
      this.resetColor(partId, mapColor)
    })
  }

  private resolveMappedColor(
    partId: string,
    colorMap: { [key: string]: number },
  ): number | undefined {
    if (partId in colorMap) return colorMap[partId]

    let currentUUID: string | undefined
    if (this.partNodeMap.has(partId)) currentUUID = partId
    else {
      const object = this.getObjectByName(partId)
      currentUUID = object ? this.objectNodeMap.get(object)?.uuid : undefined
    }

    while (currentUUID) {
      if (currentUUID in colorMap) return colorMap[currentUUID]
      currentUUID = this.partNodeMap.get(currentUUID)?.parent || undefined
    }

    return undefined
  }

  resetColor(partId: string, mapColor?: number) {
    const object = this.getObjectByName(partId)
    if (!object) return
    const forceWhite = this.isBlackAndWhiteWireframeEnabled
    const hasMapColor = mapColor !== undefined
    const shouldForceWhite = forceWhite && !hasMapColor

    object.traverse((child) => {
      if (child.type === 'Mesh' && !child.userData.isCrossSectionCap) {
        const mesh = child as Mesh

        // Normalize current material(s)
        const currentMat = mesh.material as unknown as Material | Material[]
        const currentMats: Material[] = Array.isArray(currentMat)
          ? currentMat
          : [currentMat]

        // Ensure we have original material(s) captured; fallback to current if missing
        const originalMatUD = (mesh.userData.originalMaterial ?? currentMat) as
          | Material
          | Material[]
        const originalMats: Material[] = Array.isArray(originalMatUD)
          ? originalMatUD
          : [originalMatUD]

        // Normalize original color(s). They may be `number | null` or array.
        const originalColorUD = mesh.userData.originalColor as
          | number
          | null
          | Array<number | null>
          | undefined
        const originalColors: Array<number | null> = Array.isArray(
          originalColorUD,
        )
          ? originalColorUD
          : [originalColorUD ?? null]

        const anyTransparent = currentMats.some((m) =>
          Boolean((m as any)?.transparent),
        )

        // Decide whether to clone current materials or reuse the originals
        const nextMats: Material[] =
          shouldForceWhite || hasMapColor || anyTransparent
            ? currentMats.map((m) => m.clone())
            : originalMats

        // Dispose old cloned materials to free memory (but never dispose the originals)
        currentMats.forEach((m) => {
          if (
            !originalMats.includes(m) &&
            typeof (m as any)?.dispose === 'function'
          ) {
            ;(m as any).dispose()
          }
        })

        // Apply color (only if material supports `.color`)
        nextMats.forEach((m, i) => {
          const material = m as any
          const c = material?.color
          const originalMaterial = (originalMats[i] ?? originalMats[0]) as any
          const hex = shouldForceWhite
            ? 0xffffff
            : hasMapColor
              ? mapColor
              : (originalColors[i] ?? originalColors[0])
          if (hex != null && c && typeof c.setHex === 'function') {
            c.setHex(hex)
          }

          if (
            forceWhite &&
            material?.emissive &&
            typeof material.emissive.setHex === 'function'
          ) {
            material.emissive.setHex(shouldForceWhite ? 0xffffff : 0x000000)
            if (typeof material.emissiveIntensity === 'number') {
              material.emissiveIntensity = shouldForceWhite ? 1 : 0
            }
          }

          if (forceWhite && !shouldForceWhite) {
            if (
              typeof material?.metalness === 'number' &&
              typeof originalMaterial?.metalness === 'number'
            ) {
              material.metalness = originalMaterial.metalness
            }
            if (
              typeof material?.roughness === 'number' &&
              typeof originalMaterial?.roughness === 'number'
            ) {
              material.roughness = originalMaterial.roughness
            }
          }
          if (forceWhite && typeof material?.toneMapped === 'boolean') {
            material.toneMapped = shouldForceWhite
              ? false
              : typeof originalMaterial?.toneMapped === 'boolean'
                ? originalMaterial.toneMapped
                : true
          }

          // Restore rendering flags on every material
          material.depthTest = true
          material.depthWrite = Boolean(mesh.userData.isFrontSide)
          material.needsUpdate = true
        })

        // Restore render order
        mesh.renderOrder = mesh.userData.isFrontSide
          ? FRONT_RENDER_ORDER
          : BACK_RENDER_ORDER

        // Assign back in the same shape (array vs single)
        mesh.material = Array.isArray(originalMatUD)
          ? (nextMats as unknown as Material[])
          : (nextMats[0] as unknown as Material)
      }
    })

    // If we're fully resetting (no explicit mapColor), remove from cache.
    if (!hasMapColor) this.coloredPartsCache.delete(partId)
  }

  resetHiddenParts(hiddenParts: string[]) {
    hiddenParts.forEach((partUUID) => {
      this.setVisibility(partUUID, true)
    })
  }

  /**
   * setTransparency - Sets the transparency of a part in the CAD model
   * @param partName
   */

  setTransparency(partId: string, transparency: number = 0.3) {
    const object = this.getObjectByName(partId)
    let isTransparent = false

    object?.traverse((child) => {
      if (child.type === 'Mesh' && !child.userData.isCrossSectionCap) {
        const mesh = child as Mesh
        const material = mesh.material as MeshBasicMaterial
        const matClone = material.clone()
        if (matClone.transparent) {
          matClone.opacity = 1
          matClone.transparent = false
        } else {
          matClone.transparent = true
          matClone.opacity = transparency
          isTransparent = true
        }
        mesh.material = matClone
      }
    })

    return isTransparent
  }

  resetTransparency(transparentParts: string[]) {
    transparentParts.forEach((partId) => {
      const object = this.getObjectByName(partId)
      if (!object) return

      object.traverse((child) => {
        if (child.type === 'Mesh' && !child.userData.isCrossSectionCap) {
          const mesh = child as Mesh
          const material = mesh.material as MeshBasicMaterial
          const matClone = material.clone()
          if (matClone.transparent) {
            matClone.opacity = 1
            matClone.transparent = false
            mesh.material = matClone
          }
        }
      })
    })
  }

  resetExplosions(explosions: Explosions) {
    this.resetTransformGroup()
    this.explodeParts(explosions, { reset: true })
  }

  explodeParts(explosions: Explosions, options?: { reset?: boolean }) {
    Object.entries(explosions).forEach(([partId, explosion]) => {
      const object = this.getObjectByName(this.formatPartName(partId))

      if (!object) return

      if (options?.reset) {
        this.resetObjectState(object)
      } else {
        this.saveObjectState(object)
      }

      const translation = options?.reset
        ? new Vector3().fromArray(explosion.translation).negate()
        : new Vector3().fromArray(explosion.translation)

      object.matrixAutoUpdate = false
      const current = object.position.clone()
      const explodePosition = new Vector3().addVectors(current, translation)

      const tempMatrix = new Matrix4().copy(object.matrix)
      tempMatrix.setPosition(explodePosition)
      object.position.copy(explodePosition)
      object.matrix.copy(tempMatrix)
    })
  }

  initTransformControls(controls: TransformControls) {
    if (!this.transformControls) {
      // #TODO: fix offset problem
      this.scene.attach(controls)
      this.transformControls = controls
      return this.transformControls
    }
  }

  cleanupTransformControls() {
    if (this.transformControls) {
      this.transformControls.dispose()
      this.transformControls = null
    }
  }

  initCrossSectionControl(
    type: 'translate' | 'rotate',
    controls: TransformControls,
  ) {
    if (!this.crossSectionControls[type]) {
      this.scene.attach(controls)
      this.crossSectionControls[type] = controls
    }
  }

  cleanupCrossSectionControl(type: 'translate' | 'rotate') {
    if (this.crossSectionControls[type]) {
      this.crossSectionControls[type]?.dispose()
      this.crossSectionControls[type] = null
    }
  }

  initTransformGroup(onAdded: () => void) {
    if (!this.transformGroup) {
      const transformGroup = new Group()
      transformGroup.name = 'explosions-transform-group'
      transformGroup.userData.isMerged = true
      this.transformGroup = transformGroup
    }
    this.transformGroup.addEventListener('added', onAdded)
    this.scene.add(this.transformGroup)
    return this.transformGroup
  }

  resetTransformGroup() {
    if (this.transformGroup) {
      // Return all children to their original parents before cleanup
      while (this.transformGroup.children.length) {
        const child = this.transformGroup.children[0]
        this.returnToParent(child)
      }
    }
  }

  cleanupTransformGroup(onAdded: () => void) {
    if (this.transformGroup) {
      this.transformGroup.removeEventListener('added', onAdded)
      this.resetTransformGroup()
      this.scene.remove(this.transformGroup)
      this.transformGroup = null
    }
  }

  getOriginalParent(obj?: Object3D) {
    const { originalParentName } = obj?.userData as ObjectExplosionsUserData
    return this.getObjectByName(originalParentName)
  }

  returnToParent(obj: Object3D) {
    const parent = this.getOriginalParent(obj)

    if (parent) parent.attach(obj)
    else console.error('No parent found for object', obj)
  }

  saveObjectState(obj: Object3D) {
    const parent = obj.parent
    const userData = obj.userData as ObjectExplosionsUserData

    if (parent && !userData.originalParentName) {
      userData.originalParentName = parent.name
      userData.originalParentWorldPosition = parent
        .getWorldPosition(new Vector3())
        .toArray() as Vector3Tuple
    }

    if (!userData.originalPosition) {
      userData.originalPosition = obj.position.toArray() as Vector3Tuple
    }

    if (!userData.originalWorldPosition) {
      userData.originalWorldPosition = obj
        .getWorldPosition(new Vector3())
        .toArray() as Vector3Tuple
    }
  }

  resetObjectState(obj: Object3D) {
    const userData = obj.userData as ObjectExplosionsUserData
    const parentName = userData.originalParentName
    const parent = this.getObjectByName(parentName)

    if (parent) {
      parent.attach(obj)
      obj.userData = Object.entries(userData).filter(
        ([key]) => !ExplosionsUserDataKeys.includes(key),
      )
    }
  }

  drawDragLine(instanceName: string, dir: 'x' | 'y' | 'z') {
    const targetMesh = this.getObjectByName(instanceName)
    if (!targetMesh) return

    const material = new LineBasicMaterial({
      color: 0xf3f4f6, // Blue color for the line
    })

    const boundingBox = new Box3().setFromObject(targetMesh)
    const center = new Vector3()
    boundingBox.getCenter(center)

    const size = new Vector3()
    boundingBox.getSize(size)

    // Extend the start and end points further along the X-axis

    // Adjust these points based on the targetMesh's bounding box and your desired direction
    const points: Vector3[] = []

    if (dir === 'x') {
      const lineLength = size.x * 10 // Making the line longer based on the largest dimension
      points.push(new Vector3(center.x - lineLength / 2, center.y, center.z)) // Start point extended
      points.push(new Vector3(center.x + lineLength / 2, center.y, center.z)) // End point extended
    } else if (dir === 'y') {
      const lineLength = size.y * 10 // Making the line longer based on the largest dimension
      points.push(new Vector3(center.x, center.y - lineLength / 2, center.z)) // Start point of the line along the Y-axis
      points.push(new Vector3(center.x, center.y + lineLength / 2, center.z)) // End point of the line along the Y-axis
    } else if (dir === 'z') {
      const lineLength = size.z * 10 // Making the line longer based on the largest dimension
      points.push(new Vector3(center.x, center.y, center.z - lineLength / 2)) // Start point of the line along the Z-axis
      points.push(new Vector3(center.x, center.y, center.z + lineLength / 2)) // End point of the line along the Z-axis
    }

    const geometry = new BufferGeometry().setFromPoints(points)

    const line = new Line(geometry, material)

    // Optionally, adjust the line's position to match the target mesh's position
    line.position.copy(center)

    this.clearDragLines()
    this.scene.add(line)
    this.lines.push(line)
  }

  clearDragLines() {
    this.lines.forEach((line) => {
      this.scene.remove(line)
    })
    this.lines = []
  }

  showExplosionLines(
    explosions: Explosions,
    isEnabled: boolean,
    options?: { hiddenParts?: string[] },
  ) {
    const groupName = 'explosion-lines'
    const oldGroup = this.scene.getObjectByName(groupName)
    if (oldGroup) {
      this.scene.remove(oldGroup)
    }

    if (!isEnabled) {
      return
    }

    const group = new Group()
    group.name = groupName
    group.userData.isMerged = true
    this.scene.add(group)

    const hiddenPartNames = (options?.hiddenParts || [])
      .map((partUUID) => {
        const part = this.partNodeMap.get(partUUID)
        return part?.instance
      })
      .filter((partName) => !!partName)

    Object.entries(explosions).forEach(([key, explosion]) => {
      if (
        (options?.hiddenParts && options?.hiddenParts.includes(key)) ||
        hiddenPartNames.includes(key)
      ) {
        console.log('Skipping hidden part', name)
        return
      }

      const object = isUUID(key)
        ? this.getObjectByUUID(key)
        : this.getObjectByName(this.formatPartName(key) as string)

      const tempParent = this.getObjectByName(object?.parent?.name as string)
      const parent =
        tempParent === this.transformGroup
          ? this.getOriginalParent(object)
          : tempParent

      if (!object || !parent) return

      const startPoint = parent.localToWorld(
        new Vector3(
          explosion.originalPosition[0],
          explosion.originalPosition[1],
          explosion.originalPosition[2],
        ),
      )
      const endPoint = parent.localToWorld(
        new Vector3(
          explosion.position[0],
          explosion.position[1],
          explosion.position[2],
        ),
      )

      if (startPoint.equals(endPoint)) {
        return
      }

      const geometry = new BufferGeometry().setFromPoints([
        startPoint,
        endPoint,
      ])

      const material = new LineDashedMaterial({
        color: 0xff0000,
        linewidth: 2 / 500,
        dashSize: 2 / 500,
        gapSize: 1 / 500,
      })

      const line = new Line(geometry, material)
      line.name = `explodeLine_${name}`
      line.userData.isMerged = true
      line.computeLineDistances()
      group.attach(line)
    })
  }

  isClippable(object: Object3D): object is Mesh | LineSegments | Line {
    if (object.userData.isCrossSectionCap) return false
    if (object.userData.planeObject) return false
    for (let parent = object.parent; parent; parent = parent.parent) {
      if (parent instanceof TransformControls) return false
    }

    return (
      object instanceof Mesh ||
      object instanceof LineSegments ||
      object instanceof Line
    )
  }

  private setObjectClippingPlanes(
    object: Mesh | LineSegments | Line,
    clippingPlanes: Plane[],
  ) {
    const materials = Array.isArray(object.material)
      ? object.material
      : [object.material]

    materials.forEach((material) => {
      material.clippingPlanes = clippingPlanes
    })
  }

  setPartClippingPlanes(
    clippingPlanes: Plane[],
    partName: string,
    planeIds: number[],
  ) {
    const part = this.getObjectByName(partName)
    if (part) {
      part.traverse((obj) => {
        if (this.isClippable(obj)) {
          obj.onBeforeRender = () => {
            this.setObjectClippingPlanes(
              obj,
              planeIds.map((i) => clippingPlanes[i]),
            )
          }
        }
      })
    }
  }

  resetClippingPlanes() {
    this.clearCrossSectionCaps()

    this.scene.traverse((obj) => {
      if (this.isClippable(obj)) {
        obj.onBeforeRender = () => {
          this.setObjectClippingPlanes(obj, [])
        }
      }
    })
  }

  /**
   * Sets the clipping planes for meshes in the scene. If a cross-section map is provided,
   * it will set the clipping planes for specific parts of the object based on the map.
   * Otherwise, it will apply the clipping planes to all meshes in the scene.
   */
  setClippingPlanes(clippingPlanes: Plane[], crossSectionMap?: string[][]) {
    if (!clippingPlanes) return

    this.resetClippingPlanes()
    if (clippingPlanes.length === 0) return

    // Correct clipping plane types
    clippingPlanes.forEach(
      (p, i) => (clippingPlanes[i] = new Plane(p.normal, p.constant)),
    )

    if (crossSectionMap) {
      const sectionObjectMap = convertCrossSectionMap(crossSectionMap)
      Object.entries(sectionObjectMap).forEach(([partName, planeIds]) => {
        this.setPartClippingPlanes(clippingPlanes, partName, planeIds)
      })
    } else {
      this.scene.traverse((obj) => {
        if (this.isClippable(obj)) {
          obj.onBeforeRender = () => {
            this.setObjectClippingPlanes(obj, clippingPlanes)
          }
        }
      })
    }

    this.createCrossSectionCaps(clippingPlanes, crossSectionMap)
  }

  private clearCrossSectionCaps() {
    this.crossSectionCapObjects.forEach((object) => {
      object.parent?.remove(object)
    })
    this.crossSectionCapMaterials.forEach((material) => material.dispose())
    this.crossSectionCapGeometries.forEach((geometry) => geometry.dispose())

    this.crossSectionCapObjects = []
    this.crossSectionCapMaterials = []
    this.crossSectionCapGeometries = []
  }

  private createCrossSectionCaps(
    clippingPlanes: Plane[],
    crossSectionMap?: string[][],
  ) {
    const frontMeshesByPlane = this.getFrontMeshesByClippingPlane(
      clippingPlanes,
      crossSectionMap,
    )
    const capSize = this.getCrossSectionCapSize(frontMeshesByPlane)
    let renderIndex = 0

    frontMeshesByPlane.forEach((frontMeshesByPart, planeIndex) => {
      frontMeshesByPart.forEach((frontMeshes, part) => {
        const renderOrder = -1000 + renderIndex * 2
        this.createStencilCapForMesh({
          frontMeshes,
          part,
          clippingPlanes,
          planeIndex,
          capSize,
          renderOrder,
        })
        renderIndex++
      })
    })
  }

  private getFrontMeshesByClippingPlane(
    clippingPlanes: Plane[],
    crossSectionMap?: string[][],
  ) {
    const frontMeshesByPlane = new Map<number, Map<Object3D, Mesh[]>>()
    clippingPlanes.forEach((_, index) =>
      frontMeshesByPlane.set(index, new Map()),
    )

    const addFrontMeshes = (object: Object3D, planeIds: number[]) => {
      object.traverse((child) => {
        if (
          child instanceof Mesh &&
          child.userData.isFrontSide &&
          !child.userData.isCrossSectionCap
        ) {
          planeIds.forEach((planeId) => {
            const groupedMeshes = frontMeshesByPlane.get(planeId)
            const part =
              object === this.scene ? (child.parent ?? child) : object
            const frontMeshes = groupedMeshes?.get(part) ?? []
            frontMeshes.push(child)
            groupedMeshes?.set(part, frontMeshes)
          })
        }
      })
    }

    if (crossSectionMap) {
      const sectionObjectMap = convertCrossSectionMap(crossSectionMap)
      Object.entries(sectionObjectMap).forEach(([partName, planeIds]) => {
        const part = this.getObjectByName(partName)
        if (part) addFrontMeshes(part, planeIds)
      })
    } else {
      addFrontMeshes(
        this.scene,
        clippingPlanes.map((_, index) => index),
      )
    }

    return frontMeshesByPlane
  }

  private createStencilCapForMesh({
    frontMeshes,
    part,
    clippingPlanes,
    planeIndex,
    capSize,
    renderOrder,
  }: {
    frontMeshes: Mesh[]
    part: Object3D
    clippingPlanes: Plane[]
    planeIndex: number
    capSize: number
    renderOrder: number
  }) {
    frontMeshes.forEach((frontMesh, index) => {
      this.createStencilMesh({
        frontMesh,
        clippingPlanes,
        planeIndex,
        side: BackSide,
        stencilOp: IncrementWrapStencilOp,
        renderOrder,
        clearStencilBeforeRender: index === 0,
      })
      this.createStencilMesh({
        frontMesh,
        clippingPlanes,
        planeIndex,
        side: FrontSide,
        stencilOp: DecrementWrapStencilOp,
        renderOrder,
        clearStencilBeforeRender: false,
      })
    })

    const capGeometry = new PlaneGeometry(capSize, capSize)
    const capMaterial = new MeshBasicMaterial({
      color: this.getMeshColor(frontMeshes[0]),
      side: DoubleSide,
      stencilWrite: true,
      stencilRef: 0,
      stencilFunc: NotEqualStencilFunc,
      stencilFail: ReplaceStencilOp,
      stencilZFail: ReplaceStencilOp,
      stencilZPass: ReplaceStencilOp,
      clippingPlanes: clippingPlanes.filter((_, index) => index !== planeIndex),
    })
    capMaterial.userData.outlineParameters = { visible: false }
    const capMesh = new Mesh(capGeometry, capMaterial)
    capMesh.name = `CrossSectionCap:${part.name || part.uuid}`
    capMesh.renderOrder = renderOrder + 1
    capMesh.userData.isCrossSectionCap = true
    capMesh.layers.set(DEFAULT_LAYER)
    capMesh.onBeforeRender = () => {
      capMaterial.clippingPlanes = clippingPlanes.filter(
        (_, index) => index !== planeIndex,
      )

      const plane = clippingPlanes[planeIndex]
      if (plane) this.syncMeshToPlane(capMesh, plane)
    }
    capMesh.onAfterRender = (renderer) => renderer.clearStencil()

    const plane = clippingPlanes[planeIndex]
    if (plane) this.syncMeshToPlane(capMesh, plane)

    this.scene.add(capMesh)
    this.crossSectionCapObjects.push(capMesh)
    this.crossSectionCapMaterials.push(capMaterial)
    this.crossSectionCapGeometries.push(capGeometry)
  }

  private createStencilMesh({
    frontMesh,
    clippingPlanes,
    planeIndex,
    side,
    stencilOp,
    renderOrder,
    clearStencilBeforeRender,
  }: {
    frontMesh: Mesh
    clippingPlanes: Plane[]
    planeIndex: number
    side: typeof FrontSide | typeof BackSide
    stencilOp: typeof IncrementWrapStencilOp | typeof DecrementWrapStencilOp
    renderOrder: number
    clearStencilBeforeRender: boolean
  }) {
    const stencilMaterial = new MeshBasicMaterial({
      depthWrite: false,
      depthTest: false,
      colorWrite: false,
      stencilWrite: true,
      stencilFunc: AlwaysStencilFunc,
      stencilFail: stencilOp,
      stencilZFail: stencilOp,
      stencilZPass: stencilOp,
      side,
      clippingPlanes: [clippingPlanes[planeIndex]],
    })
    const stencilMesh = new Mesh(frontMesh.geometry, stencilMaterial)
    stencilMesh.name = `CrossSectionStencil:${frontMesh.name || frontMesh.uuid}`
    stencilMesh.renderOrder = renderOrder
    stencilMesh.userData.isCrossSectionCap = true
    stencilMesh.layers.set(DEFAULT_LAYER)
    stencilMesh.onBeforeRender = (renderer) => {
      if (clearStencilBeforeRender) renderer.clearStencil()
      stencilMaterial.clippingPlanes = [clippingPlanes[planeIndex]]
    }

    frontMesh.add(stencilMesh)
    this.crossSectionCapObjects.push(stencilMesh)
    this.crossSectionCapMaterials.push(stencilMaterial)
  }

  private syncMeshToPlane(mesh: Mesh, plane: Plane) {
    const position = plane.coplanarPoint(new Vector3())
    const quaternion = new Quaternion().setFromUnitVectors(
      new Vector3(0, 0, 1),
      plane.normal,
    )

    mesh.position.copy(position)
    mesh.quaternion.copy(quaternion)
  }

  private getCrossSectionCapSize(
    frontMeshesByPlane: Map<number, Map<Object3D, Mesh[]>>,
  ) {
    const bounds = new Box3()

    frontMeshesByPlane.forEach((frontMeshesByPart) => {
      frontMeshesByPart.forEach((frontMeshes) => {
        frontMeshes.forEach((frontMesh) => {
          bounds.expandByObject(frontMesh)
        })
      })
    })

    if (bounds.isEmpty()) return 1

    const size = new Vector3()
    bounds.getSize(size)

    return Math.max(size.length() * 2, 1)
  }

  private getMeshColor(mesh: Mesh) {
    const material = Array.isArray(mesh.material)
      ? mesh.material[0]
      : mesh.material

    return (material as any)?.color?.getHex?.() ?? 0x808080
  }

  getObjectMouseCoords(
    name: string,
    camera: Camera,
    domElement: HTMLCanvasElement,
  ) {
    const obj = this.getObjectByName(name)
    if (!obj) return null

    const vec = obj.position.clone().project(camera)

    // Use clientWidth and clientHeight for accurate dimensions
    const canvasRect = domElement.getBoundingClientRect()

    const x = ((vec.x + 1) / 2) * canvasRect.width + canvasRect.left
    const y = ((-vec.y + 1) / 2) * canvasRect.height + canvasRect.top

    return { x, y }
  }
}
